import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, Eye, EyeOff, Trash2, Loader2, Search, DollarSign, Users, Pencil, X, Save } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCourses = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCourse, setEditingCourse] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/admin/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const moderateCourse = async (courseId, action) => {
    const confirmMsg = action === 'delete' 
      ? 'Are you sure you want to delete this course? This cannot be undone.'
      : `Are you sure you want to ${action} this course?`;
    
    if (!window.confirm(confirmMsg)) return;

    try {
      await axios.put(`${API}/admin/courses/${courseId}/moderate?action=${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Course ${action}ed successfully`);
      fetchCourses();
    } catch (error) {
      toast.error(`Failed to ${action} course`);
    }
  };

  const openEdit = (course) => {
    navigate(`/admin/courses/${course.id}/edit`);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { ...editForm, price: parseFloat(editForm.price) };
      await axios.put(`${API}/admin/courses/${editingCourse}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Course updated successfully');
      setEditingCourse(null);
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update course');
    } finally {
      setSaving(false);
    }
  };

  const filteredCourses = courses.filter(course => 
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.instructor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-courses">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" onClick={() => navigate('/admin')} className="-ml-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Course Management</h1>
              <p className="text-slate-600">Manage, edit, and moderate all platform courses</p>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by title or instructor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="admin-courses-search"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading">All Courses ({filteredCourses.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {filteredCourses.length > 0 ? (
                  <div className="divide-y divide-slate-200">
                    {filteredCourses.map((course) => (
                      <div key={course.id} className="py-4" data-testid={`course-row-${course.id}`}>
                        {editingCourse === course.id ? (
                          /* Edit Mode */
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-4" data-testid="course-edit-form">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-medium text-slate-900">Editing Course</h3>
                              <Button variant="ghost" size="sm" onClick={() => setEditingCourse(null)} data-testid="course-edit-cancel">
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Title</Label>
                                <Input
                                  value={editForm.title}
                                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                  data-testid="course-edit-title"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Price ($)</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={editForm.price}
                                  onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                                  data-testid="course-edit-price"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Category</Label>
                                <Input
                                  value={editForm.category}
                                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                                  data-testid="course-edit-category"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Thumbnail URL</Label>
                                <Input
                                  value={editForm.thumbnail_url}
                                  onChange={(e) => setEditForm({ ...editForm, thumbnail_url: e.target.value })}
                                  placeholder="https://..."
                                  data-testid="course-edit-thumbnail"
                                />
                              </div>
                              <div className="md:col-span-2 space-y-2">
                                <Label>Description</Label>
                                <textarea
                                  value={editForm.description}
                                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                  rows={3}
                                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                  data-testid="course-edit-description"
                                />
                              </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                              <Button variant="outline" size="sm" onClick={() => setEditingCourse(null)}>
                                Cancel
                              </Button>
                              <Button size="sm" onClick={saveEdit} disabled={saving} data-testid="course-edit-save">
                                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                                Save Changes
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <div className="flex items-center gap-4">
                            <img 
                              src={course.thumbnail_url || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=100'} 
                              alt={course.title}
                              className="w-20 h-14 rounded-lg object-cover"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-slate-900 truncate">{course.title}</h3>
                                <Badge variant={course.is_published ? 'default' : 'secondary'}>
                                  {course.is_published ? 'Published' : 'Draft'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-slate-500">
                                <span>By: {course.instructor_name || 'Unknown'}</span>
                                <span className="flex items-center gap-1">
                                  <DollarSign className="w-3 h-3" />
                                  ${course.price}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {course.students_count || 0} students
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openEdit(course)}
                                data-testid={`course-edit-btn-${course.id}`}
                              >
                                <Pencil className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => navigate(`/courses/${course.id}`)}
                              >
                                View
                              </Button>
                              {course.is_published ? (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => moderateCourse(course.id, 'unpublish')}
                                >
                                  <EyeOff className="w-4 h-4 mr-1" />
                                  Unpublish
                                </Button>
                              ) : (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => moderateCourse(course.id, 'publish')}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Publish
                                </Button>
                              )}
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => moderateCourse(course.id, 'delete')}
                                className="text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No courses found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCourses;
